import {
  ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY,
  HOTEL_ADDITIONAL_DETAILS_KEYS,
  HOTEL_PRODUCT_TYPE,
  OUTBOUND,
  PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY
} from 'constants/booking';
import { ASSISTANCE_KEYS, PASSENGER_DISABILITY_KEYS } from 'constants/assistance';
import { registeredLuggageProductCodes } from 'constants/luggage';

import moment from 'moment';

export const getErrorKey = ({ errors, hotelInfo, hotelLoading, getHotelCalled, currency, hasErrors }) => {
  if (errors?.jacTravelRef) {
    return 'extras.hotel.modal.jacTravelReferenceValidationError';
  } else if (errors?.hotelId) {
    return 'extras.hotel.modal.hotelIdValidationError';
  } else if (!hotelInfo && !hotelLoading && getHotelCalled) {
    return 'extras.hotel.modal.hotelNotFoundError';
  } else if (hotelInfo?.status === 'Cancelled') {
    return 'extras.hotel.modal.hotelCancelledError';
  } else if (hotelInfo?.status === 'Closed') {
    return 'extras.hotel.modal.hotelClosedError';
  } else if (hotelInfo && hotelInfo.currency !== currency && !hotelLoading) {
    return 'extras.hotel.modal.hotelCurrencyMismatchError';
  } else if (moment().isAfter(moment(hotelInfo?.arrivalDate))) {
    return 'extras.hotels.modal.checkInDateInPast';
  } else if (hasErrors) {
    return 'extras.hotel.modal.errorAdd';
  }
  return null;
};

export const buildAdditionalDetails = ({ itemRefId, hotelInfo }) => {
  const additionalDetails = [
    {
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.PRODUCT_TYPE,
      value: HOTEL_PRODUCT_TYPE[hotelInfo.productType]
    }
  ];
  if (hotelInfo.leadGuestLastName) {
    additionalDetails.push({
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.BOOKER_SURNAME,
      value: hotelInfo.leadGuestLastName
    });
  }
  if (hotelInfo.hotelName) {
    additionalDetails.push({
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.HOTEL_NAME,
      value: hotelInfo.hotelName
    });
  }
  if (hotelInfo.duration) {
    additionalDetails.push({
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.NUMBER_OF_NIGHTS,
      value: hotelInfo.duration.toString()
    });
  }
  if (hotelInfo.numberOfGuests) {
    additionalDetails.push({
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.GUEST_COUNT,
      value: hotelInfo.numberOfGuests.toString()
    });
  }
  if (hotelInfo.numberOfRooms) {
    additionalDetails.push({
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.ROOM_COUNT,
      value: hotelInfo.numberOfRooms.toString()
    });
  }
  if (hotelInfo.address) {
    additionalDetails.push({
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.HOTEL_ADDRESS,
      value: hotelInfo.address
    });
  }
  if (hotelInfo.country) {
    additionalDetails.push({
      itemRefId,
      key: HOTEL_ADDITIONAL_DETAILS_KEYS.HOTEL_COUNTRY,
      value: hotelInfo.country
    });
  }

  return additionalDetails;
};

const findCreatedExtras = (extras, code, passengerId) =>
  extras.filter((extra) => extra.passengerId === passengerId && extra.code === code && !extra.cancelled);

export const buildAssistanceAdditionalDetails = ({ addedAssistance = [], outboundExtras = [], inboundExtras = [] }) => {
  const additionalDetails = [];

  addedAssistance.forEach(({ id, items = [] }) => {
    const isDogAssistance = id === ASSISTANCE_KEYS.GUIDE_DOG_ASSIST;

    items.forEach(({ assistanceAnimal, comment, direction, passengerDisability, passengerId }) => {
      if (isDogAssistance && !assistanceAnimal) {
        return;
      }

      const createdExtras = findCreatedExtras(direction === OUTBOUND ? outboundExtras : inboundExtras, id, passengerId);

      createdExtras.forEach((extra) => {
        const itemRefId = parseInt(extra.itemRef, 10);

        if (isDogAssistance) {
          additionalDetails.push({
            itemRefId,
            key: ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY,
            value: assistanceAnimal
          });

          if (comment != null && comment !== '') {
            additionalDetails.push({ itemRefId, key: 'MAAS', value: comment });
          }

          return;
        }

        if (passengerDisability && passengerDisability !== PASSENGER_DISABILITY_KEYS.NONE) {
          additionalDetails.push({
            itemRefId,
            key: PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY,
            value: passengerDisability
          });
        }
      });
    });
  });

  return additionalDetails;
};

export const buildAssistanceUpdatedDetails = ({ updatedAssistance = [] }) => {
  const detailsToAdd = [];
  const detailsToUpdate = [];

  updatedAssistance.forEach(({ id, itemRef, assistanceAnimal, comment, passengerDisability, initialDetails = {} }) => {
    const isDogAssistance = id === ASSISTANCE_KEYS.GUIDE_DOG_ASSIST;
    const itemRefId = parseInt(itemRef, 10);
    const detailsFor = (initialValue) => (initialValue ? detailsToUpdate : detailsToAdd);

    if (isDogAssistance) {
      if (assistanceAnimal) {
        detailsFor(initialDetails.assistanceAnimal).push({
          itemRefId,
          key: ASSISTANCE_ANIMAL_ADDITIONAL_DETAILS_KEY,
          value: assistanceAnimal
        });
      }

      if (comment != null && comment !== '') {
        detailsFor(initialDetails.comment).push({ itemRefId, key: 'MAAS', value: comment });
      }

      return;
    }

    if (passengerDisability && passengerDisability !== PASSENGER_DISABILITY_KEYS.NONE) {
      detailsFor(initialDetails.passengerDisability).push({
        itemRefId,
        key: PASSENGER_DISABILITY_ADDITIONAL_DETAILS_KEY,
        value: passengerDisability
      });
    }
  });

  return { detailsToAdd, detailsToUpdate };
};

const createSegmentMapping = (luggageJourneys, bookingSegments) => {
  const mapping = [];

  luggageJourneys?.forEach((journey, journeyIndex) => {
    journey.fares?.forEach((fare) => {
      fare.legs?.forEach((leg, legIndex) => {
        const matchingSegment = bookingSegments.find(
          (segment) => segment.trainNumber === leg.serviceName && segment.timing.date === leg.timing?.date
        );

        if (matchingSegment) {
          mapping.push({
            journeyIndex,
            legIndex,
            leg,
            fare,
            bookingSegment: matchingSegment
          });
        }
      });
    });
  });

  return mapping;
};

export const transformLuggageToExtras = (luggageProducts, outbound = [], inbound = [], shouldFilterOutUnrequiredProducts = false) => {
  if (!luggageProducts?.journeySearchForLuggageExchange) {
    return { outbound: [], inbound: [] };
  }

  const { outbound: luggageOutbound, inbound: luggageInbound } = luggageProducts.journeySearchForLuggageExchange;

  const outboundMapping = createSegmentMapping(luggageOutbound?.journeys, outbound);
  const inboundMapping = createSegmentMapping(luggageInbound?.journeys, inbound);

  const outboundExtrasMap = new Map();
  const inboundExtrasMap = new Map();

  const getAvailableUnits = ({ availabilityOfClassOfService, physicalAvailability, inventoryUnits }) => {
    const classAvailability = availabilityOfClassOfService ?? physicalAvailability ?? 1;
    const inventory = inventoryUnits ?? 1;

    return Math.floor(classAvailability / inventory);
  };

  outboundMapping.forEach(({ leg, bookingSegment }) => {
    leg.addOnProducts?.forEach((product) => {
      const {
        tariffCode,
        name,
        price,
        originalPrice,
        passengerId,
        passengerAgeGroup,
        availabilityOfClassOfService,
        physicalAvailability,
        inventoryUnits,
        code
      } = product;

      if ((shouldFilterOutUnrequiredProducts && registeredLuggageProductCodes.includes(code)) || !shouldFilterOutUnrequiredProducts) {
        const segmentId = bookingSegment.id;

        const physical = physicalAvailability ?? 1;
        const inventory = inventoryUnits ?? 1;
        const availableUnits = getAvailableUnits({ availabilityOfClassOfService, physicalAvailability, inventoryUnits });

        const uniqueKey = `${tariffCode}__${leg.serviceIdentifier}`;

        if (!outboundExtrasMap.has(uniqueKey)) {
          outboundExtrasMap.set(uniqueKey, {
            id: tariffCode,
            name,
            passengerTypes: new Set(),
            passengers: new Set(),
            segments: new Set(),
            paxSegmentsMap: new Map(),
            price: originalPrice || price,
            direction: 'outbound',
            physicalAvailability: physical,
            inventoryUnits: inventory,
            availableUnits: availableUnits,
            serviceName: leg.serviceName,
            serviceIdentifier: leg.serviceIdentifier,
            origin: leg.origin?.uic,
            destination: leg.destination?.uic,
            startValidityDate: leg.timing?.date
          });
        }

        const extra = outboundExtrasMap.get(uniqueKey);

        if (passengerAgeGroup?.type) {
          extra.passengerTypes.add(passengerAgeGroup.type);
        }

        if (passengerId) {
          extra.passengers.add(passengerId);
        }

        if (segmentId) {
          extra.segments.add(segmentId);
        }

        if (passengerId && segmentId) {
          if (!extra.paxSegmentsMap.has(passengerId)) {
            extra.paxSegmentsMap.set(passengerId, new Set());
          }
          extra.paxSegmentsMap.get(passengerId).add(segmentId);
        }
      }
    });
  });

  inboundMapping.forEach(({ leg, bookingSegment }) => {
    leg.addOnProducts?.forEach((product) => {
      const {
        tariffCode,
        name,
        price,
        originalPrice,
        passengerId,
        passengerAgeGroup,
        availabilityOfClassOfService,
        physicalAvailability,
        inventoryUnits,
        code
      } = product;

      if ((shouldFilterOutUnrequiredProducts && registeredLuggageProductCodes.includes(code)) || !shouldFilterOutUnrequiredProducts) {
        const segmentId = bookingSegment.id;

        const physical = physicalAvailability ?? 1;
        const inventory = inventoryUnits ?? 1;
        const availableUnits = getAvailableUnits({ availabilityOfClassOfService, physicalAvailability, inventoryUnits });

        const uniqueKey = `${tariffCode}__${leg.serviceIdentifier}`;

        if (!inboundExtrasMap.has(uniqueKey)) {
          inboundExtrasMap.set(uniqueKey, {
            id: tariffCode,
            name,
            passengerTypes: new Set(),
            passengers: new Set(),
            segments: new Set(),
            paxSegmentsMap: new Map(),
            price: originalPrice || price,
            direction: 'inbound',
            physicalAvailability: physical,
            inventoryUnits: inventory,
            availableUnits: availableUnits,
            serviceName: leg.serviceName,
            serviceIdentifier: leg.serviceIdentifier,
            origin: leg.origin?.uic,
            destination: leg.destination?.uic,
            startValidityDate: leg.timing?.date
          });
        }

        const extra = inboundExtrasMap.get(uniqueKey);

        if (passengerAgeGroup?.type) {
          extra.passengerTypes.add(passengerAgeGroup.type);
        }

        if (passengerId) {
          extra.passengers.add(passengerId);
        }

        if (segmentId) {
          extra.segments.add(segmentId);
        }

        if (passengerId && segmentId) {
          if (!extra.paxSegmentsMap.has(passengerId)) {
            extra.paxSegmentsMap.set(passengerId, new Set());
          }
          extra.paxSegmentsMap.get(passengerId).add(segmentId);
        }
      }
    });
  });

  const mapToExtras = (extrasMap) => {
    const extras = Array.from(extrasMap.values()).map((extra) => ({
      id: extra.id,
      name: extra.name,
      passengerTypes: Array.from(extra.passengerTypes),
      passengers: Array.from(extra.passengers),
      segments: Array.from(extra.segments),
      paxSegments: Array.from(extra.paxSegmentsMap.entries()).map(([passengerId, segmentsSet]) => ({
        passengerId,
        segments: Array.from(segmentsSet)
      })),
      price: extra.price,
      direction: extra.direction,
      physicalAvailability: extra.physicalAvailability,
      inventoryUnits: extra.inventoryUnits,
      availableUnits: extra.availableUnits,
      serviceName: extra.serviceName,
      serviceIdentifier: extra.serviceIdentifier,
      origin: extra.origin,
      destination: extra.destination,
      startValidityDate: extra.startValidityDate
    }));

    return extras.sort((a, b) => a.name.localeCompare(b.name));
  };

  return {
    outbound: mapToExtras(outboundExtrasMap),
    inbound: mapToExtras(inboundExtrasMap)
  };
};

export const getExtrasLimitExceededDirections = (
  formPayload,
  outboundExtras,
  inboundExtras,
  outboundExtrasOptions,
  inboundExtrasOptions
) => {
  let outboundUnitsUsed = 0;
  let inboundUnitsUsed = 0;

  formPayload.add.forEach((addItem) => {
    const outboundExtra = outboundExtrasOptions.find((extra) => extra.id === addItem.id);
    const inboundExtra = inboundExtrasOptions.find((extra) => extra.id === addItem.id);

    const outboundItemsCount = addItem.items.filter((item) => item.direction === 'outbound').length;
    const inboundItemsCount = addItem.items.filter((item) => item.direction === 'inbound').length;

    if (outboundExtra && outboundItemsCount > 0) {
      outboundUnitsUsed += outboundItemsCount * (outboundExtra.inventoryUnits || 1);
    }
    if (inboundExtra && inboundItemsCount > 0) {
      inboundUnitsUsed += inboundItemsCount * (inboundExtra.inventoryUnits || 1);
    }
  });

  formPayload.remove.forEach((itemRef) => {
    const isOutboundExtra = outboundExtras.find((extra) => extra.itemRef === itemRef);
    const isInboundExtra = inboundExtras.find((extra) => extra.itemRef === itemRef);

    if (isOutboundExtra) {
      const tariffCode = isOutboundExtra.tariffDetails?.tariffCode;
      const extraOption = outboundExtrasOptions.find((opt) => opt.id === tariffCode);
      outboundUnitsUsed -= extraOption?.inventoryUnits || 1;
    }
    if (isInboundExtra) {
      const tariffCode = isInboundExtra.tariffDetails?.tariffCode;
      const extraOption = inboundExtrasOptions.find((opt) => opt.id === tariffCode);
      inboundUnitsUsed -= extraOption?.inventoryUnits || 1;
    }
  });

  const getDirectionAvailability = (extraOptions = []) => {
    if (extraOptions.length === 0) {
      return 0;
    }

    const option = extraOptions[0];

    if (option.availableUnits !== undefined) {
      return option.availableUnits * (option.inventoryUnits || 1);
    }

    return option.physicalAvailability || 0;
  };

  const outboundAvailability = getDirectionAvailability(outboundExtrasOptions);
  const inboundAvailability = getDirectionAvailability(inboundExtrasOptions);

  return {
    hasExceededOutboundLimit: outboundUnitsUsed > outboundAvailability,
    hasExceededInboundLimit: inboundUnitsUsed > inboundAvailability
  };
};
